import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, TrendingUp, Brain, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import { API } from "../config/api";

const PricePredictionCompact = ({ ticker }) => {
  const [predictionToday, setPredictionToday] = useState(null);
  const [prediction5d, setPrediction5d] = useState(null);
  const [prediction14d, setPrediction14d] = useState(null);
  const [prediction30d, setPrediction30d] = useState(null);
  const [loadingToday, setLoadingToday] = useState(false);
  const [loading5d, setLoading5d] = useState(false);
  const [loading14d, setLoading14d] = useState(false);
  const [loading30d, setLoading30d] = useState(false);
  const [show14d, setShow14d] = useState(false);
  const [show30d, setShow30d] = useState(false);

  useEffect(() => {
    if (ticker) {
      setPredictionToday(null);
      setPrediction5d(null);
      setPrediction14d(null);
      setPrediction30d(null);
      setShow14d(false);
      setShow30d(false);
      fetchPredictionToday();
      fetchPrediction5d();
    }
  }, [ticker]);

  const fetchPredictionToday = async () => {
    setLoadingToday(true);
    try {
      const response = await axios.get(`${API}/stock/price-prediction-today/${ticker}`);
      setPredictionToday(response.data);
    } catch (error) {
      console.error("Error fetching today prediction:", error);
      console.warn("Failed to load today's prediction - retrying silently in background");
    } finally {
      setLoadingToday(false);
    }
  };

  const fetchPrediction5d = async () => {
    setLoading5d(true);
    try {
      const response = await axios.get(`${API}/stock/price-prediction/${ticker}?days=5`);
      setPrediction5d(response.data);
    } catch (error) {
      console.error("Error fetching 5-day prediction:", error);
      console.warn("Failed to load 5-day predictions - retrying silently in background");
    } finally {
      setLoading5d(false);
    }
  };

  const fetchPrediction14d = async () => {
    if (prediction14d) {
      setShow14d(!show14d);
      return;
    }
    setLoading14d(true);
    setShow14d(true);
    try {
      const response = await axios.get(`${API}/stock/price-prediction/${ticker}?days=14`);
      setPrediction14d(response.data);
    } catch (error) {
      console.error("Error fetching 14-day prediction:", error);
      console.warn("Failed to load 14-day predictions - retrying silently in background");
      setShow14d(false);
    } finally {
      setLoading14d(false);
    }
  };

  const fetchPrediction30d = async () => {
    if (prediction30d) {
      setShow30d(!show30d);
      return;
    }
    setLoading30d(true);
    setShow30d(true);
    try {
      const response = await axios.get(`${API}/stock/price-prediction/${ticker}?days=30`);
      setPrediction30d(response.data);
    } catch (error) {
      console.error("Error fetching 30-day prediction:", error);
      console.warn("Failed to load 30-day predictions - retrying silently in background");
      setShow30d(false);
    } finally {
      setLoading30d(false);
    }
  };

  const prepareChartData = (prediction) => {
    if (!prediction?.predictions || !prediction?.dates) return [];
    
    const numDays = prediction.days || 5;
    const days = Array.from({length: numDays}, (_, i) => `day_${i + 1}`);
    return days.map((day) => ({
      day: prediction.dates[day]?.formatted || day,
      consensus: prediction.predictions.consensus?.[day]?.price || null,
      current: prediction.current_price
    }));
  };

  const calculatePriceChange = (predictedPrice, currentPrice) => {
    return ((predictedPrice - currentPrice) / currentPrice) * 100;
  };

  const getConfidenceBadge = (confidence) => {
    const colors = {
      High: 'bg-green-100 text-green-700',
      Medium: 'bg-yellow-100 text-yellow-700',
      Low: 'bg-red-100 text-red-700'
    };
    return colors[confidence] || 'bg-gray-100 text-gray-700';
  };

  const renderCompactTable = (prediction) => {
    if (!prediction) return null;
    const numDays = prediction.days || 5;
    const days = Array.from({length: numDays}, (_, i) => `day_${i + 1}`);
    
    return (
      <div className="overflow-x-auto max-h-[400px] overflow-y-auto border border-slate-200 rounded-lg">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 sticky top-0">
            <tr>
              <th className="text-left p-2">Date</th>
              <th className="text-center p-2">Consensus Price</th>
              <th className="text-center p-2">Change</th>
              <th className="text-center p-2">Confidence</th>
              <th className="text-left p-2">Key Factors</th>
            </tr>
          </thead>
          <tbody>
            {days.map((day, idx) => {
              const dateInfo = prediction.dates?.[day];
              const consensusData = prediction.predictions.consensus?.[day];
              if (!consensusData) return null;
              
              return (
                <tr key={day} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className="p-2">
                    <div className="font-medium">{dateInfo?.formatted}</div>
                    <div className="text-[10px] text-slate-500">{dateInfo?.day_name}</div>
                  </td>
                  <td className="p-2 text-center font-bold text-slate-900">
                    ${consensusData.price.toFixed?.(2) || "0.00"}
                  </td>
                  <td className={`p-2 text-center font-semibold ${calculatePriceChange(consensusData.price, prediction.current_price) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {calculatePriceChange(consensusData.price, prediction.current_price) >= 0 ? '+' : ''}
                    {calculatePriceChange(consensusData.price, prediction.current_price).toFixed?.(2) || "0.00"}%
                  </td>
                  <td className="p-2 text-center">
                    <Badge className={`${getConfidenceBadge(consensusData.confidence)} text-[9px]`}>
                      {consensusData.confidence}
                    </Badge>
                  </td>
                  <td className="p-2 text-[10px] text-slate-600">
                    {consensusData.factors.slice(0, 2).join(", ")}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Today's Prediction - Fastest */}
      <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-600" />
            Today's Closing Price Prediction
          </CardTitle>
          <p className="text-xs text-slate-600">Fast AI prediction for today's close</p>
        </CardHeader>
        <CardContent>
          {loadingToday ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
              <span className="ml-2 text-sm text-slate-600">Predicting...</span>
            </div>
          ) : predictionToday ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-white border border-purple-200 rounded-lg">
                <p className="text-xs text-slate-600 mb-1">Current Price</p>
                <p className="text-2xl font-bold text-slate-900">${predictionToday.current_price}</p>
              </div>
              <div className="p-4 bg-white border border-purple-200 rounded-lg">
                <p className="text-xs text-slate-600 mb-1">Predicted Close</p>
                <p className="text-2xl font-bold text-purple-700">
                  ${predictionToday.prediction.price.toFixed?.(2) || "0.00"}
                </p>
                <p className={`text-xs font-semibold mt-1 ${
                  ((predictionToday.prediction.price - predictionToday.current_price) / predictionToday.current_price * 100) >= 0 
                    ? 'text-green-600' 
                    : 'text-red-600'
                }`}>
                  {((predictionToday.prediction.price - predictionToday.current_price) / predictionToday.current_price * 100) >= 0 ? '+' : ''}
                  {(((predictionToday.prediction.price - predictionToday.current_price) / predictionToday.current_price) * 100).toFixed?.(2) || "0.00"}%
                </p>
              </div>
              <div className="p-4 bg-white border border-purple-200 rounded-lg">
                <p className="text-xs text-slate-600 mb-1">Confidence</p>
                <Badge className={`${getConfidenceBadge(predictionToday.prediction.confidence)} text-sm`}>
                  {predictionToday.prediction.confidence}
                </Badge>
                <p className="text-xs text-slate-600 mt-2">{predictionToday.prediction.reason}</p>
              </div>
            </div>
          ) : <p className="text-center text-slate-600 py-4 text-sm">No data available</p>}
        </CardContent>
      </Card>

      {/* 5-Day Prediction */}
      <Card className="border-slate-200 bg-white/80 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-600" />
            Next 5 Days - Quick Forecast
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading5d ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
              <span className="ml-2 text-sm text-slate-600">Loading...</span>
            </div>
          ) : prediction5d ? (
            <div className="space-y-4">
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={prepareChartData(prediction5d)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v.toFixed?.(0) || "0"}`} />
                  <Tooltip formatter={(v) => `$${v?.toFixed?.(2) || "0.00"}`} />
                  <Legend />
                  <Line type="monotone" dataKey="current" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" name="Current" dot={false} />
                  <Line type="monotone" dataKey="consensus" stroke="#ef4444" strokeWidth={2.5} name="Predicted" />
                </LineChart>
              </ResponsiveContainer>
              {renderCompactTable(prediction5d)}
            </div>
          ) : <p className="text-center text-slate-600 py-4">No data available</p>}
        </CardContent>
      </Card>

      {/* 14-Day Prediction */}
      <Card className="border-slate-200 bg-white/80 backdrop-blur-sm">
        <CardHeader className="cursor-pointer" onClick={fetchPrediction14d}>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Next 2 Weeks (14 Days)
            </div>
            <Button variant="outline" size="sm" className="text-xs">
              {loading14d ? <Loader2 className="w-4 h-4 animate-spin" /> : show14d ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {loading14d ? "Loading..." : show14d ? "Hide" : "Show"}
            </Button>
          </CardTitle>
        </CardHeader>
        {show14d && (
          <CardContent>
            {prediction14d ? (
              <div className="space-y-4">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={prepareChartData(prediction14d)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v.toFixed?.(0) || "0"}`} />
                    <Tooltip formatter={(v) => `$${v?.toFixed?.(2) || "0.00"}`} />
                    <Legend />
                    <Line type="monotone" dataKey="current" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" name="Current" dot={false} />
                    <Line type="monotone" dataKey="consensus" stroke="#3b82f6" strokeWidth={2.5} name="Predicted" />
                  </LineChart>
                </ResponsiveContainer>
                {renderCompactTable(prediction14d)}
              </div>
            ) : null}
          </CardContent>
        )}
      </Card>

      {/* 30-Day Prediction */}
      <Card className="border-slate-200 bg-white/80 backdrop-blur-sm">
        <CardHeader className="cursor-pointer" onClick={fetchPrediction30d}>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Next Month (30 Days)
            </div>
            <Button variant="outline" size="sm" className="text-xs">
              {loading30d ? <Loader2 className="w-4 h-4 animate-spin" /> : show30d ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {loading30d ? "Loading..." : show30d ? "Hide" : "Show"}
            </Button>
          </CardTitle>
        </CardHeader>
        {show30d && (
          <CardContent>
            {prediction30d ? (
              <div className="space-y-4">
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={prepareChartData(prediction30d)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" tick={{ fontSize: 9 }} angle={-45} textAnchor="end" height={70} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v.toFixed?.(0) || "0"}`} />
                    <Tooltip formatter={(v) => `$${v?.toFixed?.(2) || "0.00"}`} />
                    <Legend />
                    <Line type="monotone" dataKey="current" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" name="Current" dot={false} />
                    <Line type="monotone" dataKey="consensus" stroke="#10b981" strokeWidth={2.5} name="Predicted" />
                  </LineChart>
                </ResponsiveContainer>
                {renderCompactTable(prediction30d)}
              </div>
            ) : null}
          </CardContent>
        )}
      </Card>

      {/* Disclaimer */}
      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-[10px] text-slate-700">
          <strong>⚠️ Disclaimer:</strong> AI predictions (GPT-5, Gemini 2.5 Pro, Claude Sonnet 4) based on historical data. For informational purposes only. Not financial advice.
        </p>
      </div>
    </div>
  );
};

export default PricePredictionCompact;
